import docCookies from 'doc-cookies';
import { load } from 'recaptcha-v3';

import {
  formatedDay,
  formatedMonth,
  formatedMoneyWithFree,
  locale,
  initInputs,
} from '../alpine-functions';

import { BOTH_TYPE, IN_PERSON_TYPE, BASE_URL_SANCTUM } from '../misc/consts';
import { readStoredCart, writeStoredCart } from '../misc/cart-storage';

const PAYMENT_ERROR_TIMEOUT = 10000;
const CART_EVENTS_URL = '/api/v1/cart/events';
// A cart refreshed longer ago than this is refreshed again before an order
// is sent, so a tab left open does not send yesterday's prices.
const CART_REFRESH_MAX_AGE = 2 * 60 * 1000;
const DEFAULT_VAT_RATE = 21;
const DEFAULT_COUNTRY = 'CZ';
const VAT_LOOKUP_DEBOUNCE = 600;
// EU member states other than Czechia: a VAT payer billed from one of these is
// reverse charged once VIES verifies their VAT ID (App\Support\Countries).
const REVERSE_CHARGE_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
];
const RECAPTCHA_SITE_KEY = process.env.MIX_RECAPTCHA_SITE_KEY;

// Reasons an order can be refused that have their own message in the cart.
const ORDER_ERROR_KEYS = [
  'price_changed',
  'total_changed',
  'pricing_unavailable',
  'pricing_failed',
  'payment_cancelled',
];

// A term is still on sale on the morning it starts — the events API keeps
// offering it, and the course page lists it — so only a date before today is
// past.
//
// The date is compared as text. `start` is "2026-09-16", and `new Date()` reads
// a bare date as midnight UTC, which is already behind us at every hour of a
// Czech day: comparing the two instants dropped a course starting today out of
// the cart the moment it was added.
const startedBeforeToday = (item) => {
  const start = String(item && item.start ? item.start : '').slice(0, 10);

  // A date we cannot read is not a date in the past; whether the term is still
  // on sale is then the server's answer, not ours.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) {
    return false;
  }

  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return start < `${now.getFullYear()}-${month}-${day}`;
};

// What the cart keeps: a term that has not started yet, carrying an event id
// the server can price. Without that id nothing ever asks about the item —
// refreshCart() only looks up positive ids — so it would sit in the cart
// forever, and no order could be built from it.
const canStillBeOrdered = (item) =>
  Number(item?.id) > 0 && !startedBeforeToday(item);

// A new order id like the {{ custom_form_id }} tag makes (six characters,
// upper case). The server refuses an id that is already taken.
const newCustomFormId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

// create-checkout-session error codes, mapped to the message keys above.
const CHECKOUT_ERROR_KEYS = {
  price_mismatch: 'price_changed',
  pricing_unavailable: 'pricing_unavailable',
  pricing_failed: 'pricing_failed',
};

export default () => ({
  cart: [],
  locale: locale(),
  paymentMethod: null,
  step: Alpine.$persist(1).as('cartStep').using(sessionStorage),
  customFormId: null,
  // The order the server re-priced (create-checkout-session answered
  // total_changed): paying again goes on with it instead of sending the
  // form again, which would create a second order.
  checkoutPendingId: null,
  totalChangedGross: '',
  redirectingToStripe: false,
  showPaymentError: false,
  paymentErrorTimeout: null,
  vatRate: DEFAULT_VAT_RATE,
  vatRateTimeout: null,
  vatRateRequestId: 0,
  // What the customer is told about their VAT ID: null (nothing to say),
  // 'checking', 'verified', 'invalid', 'mismatch' or 'unavailable'.
  vatCheck: { state: null, name: null },
  cartRefresh: Promise.resolve({ complete: true, notFound: false }),
  cartRefreshedAt: 0,
  checkingCart: false,
  removedServices: {},
  paymentErrorKey: null,

  getCart() {
    const saved = readStoredCart();

    // Courses that already started, and anything too broken to price, are
    // dropped before a single request is made for them.
    this.cart = saved.filter((item) => canStillBeOrdered(item));

    this.cart.forEach((item) => {
      if (!item.optionalServices) {
        item.optionalServices = [];
        item.attendees = 1;
      }
      // Saved by an earlier version of the cart, which kept the notice here.
      delete item.removedServices;
    });

    // The header counts what is in localStorage, so what is left is written
    // back rather than only dropped from the page. $watch('cart') is only
    // registered once init() is through, so nothing else does it here.
    if (this.cart.length !== saved.length) {
      writeStoredCart(this.cart);
      this.$dispatch('cart-updated', true);
    }

    this.cartRefresh = this.refreshCart();

    this.$nextTick(() => {
      initTooltipster();
    });
  },

  // Items are saved to localStorage when they are added, possibly days ago.
  // The server rebuilds the order from the events API and refuses a total the
  // customer was not shown, so every item is brought up to date from what the
  // server prices from (/api/v1/cart/events) first.
  //
  // Resolves to { complete, notFound }: whether every item could be refreshed,
  // and whether any of them is an event that can no longer be ordered.
  async refreshCart() {
    const result = { complete: true, notFound: false };
    const ids = [
      ...new Set(
        this.cart.map((item) => Number(item.id)).filter((id) => id > 0)
      ),
    ];

    if (ids.length === 0) {
      this.cartRefreshedAt = Date.now();
      return result;
    }

    let events = {};
    try {
      const response = await fetch(`${CART_EVENTS_URL}?ids=${ids.join(',')}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      events = response.ok ? (await response.json()).events ?? {} : {};
    } catch (error) {
      events = {};
    }

    let descriptionChanged = false;

    for (const item of this.cart) {
      const answer = events[item.id];

      if (answer?.status !== 'ok') {
        result.complete = false;
        result.notFound = result.notFound || answer?.status === 'not_found';
        continue;
      }

      try {
        descriptionChanged =
          (await this.refreshCartItem(item, answer.event)) ||
          descriptionChanged;
      } catch (error) {
        console.error('Could not refresh a cart item:', error);
        result.complete = false;
      }
    }

    if (descriptionChanged) {
      // The term, attendance and language selects are built once, when a
      // card is rendered. Render the cards afresh so they show the new term.
      const items = this.cart;
      this.cart = [];
      await this.$nextTick();
      this.cart = items;
    }

    if (result.complete) {
      this.cartRefreshedAt = Date.now();
    }

    this.$nextTick(() => {
      initTooltipster();
    });

    return result;
  },

  // Brings one item in line with what CartPricingService charges for it.
  // Resolves to whether the term itself (date, city, form, language) changed.
  async refreshCartItem(item, event) {
    const offered = (
      Array.isArray(event.services) ? event.services : []
    ).filter((service) => service && Number(service.id) > 0);
    const current = (
      Array.isArray(item.optionalServices) ? item.optionalServices : []
    ).filter((service) => service && typeof service === 'object');
    const offeredIds = new Set(offered.map((service) => Number(service.id)));

    const removed = current.filter(
      (service) => service.count > 0 && !offeredIds.has(Number(service.id))
    );
    if (removed.length > 0) {
      this.removedServices = {
        ...this.removedServices,
        [item.id]: removed.map((service) => service.name),
      };
    }

    const sameServices =
      current.length === item.optionalServices?.length &&
      current.length === offered.length &&
      offered.every(
        (service, index) => Number(current[index].id) === Number(service.id)
      );

    if (sameServices) {
      // Update in place, so the rendered rows keep their checkbox state.
      offered.forEach((service, index) => {
        Object.assign(current[index], {
          name: service.name,
          description: service.description,
          price: service.price,
          price_eur: service.price_eur,
          vat_regime: service.vat_regime,
        });
      });
    } else {
      // The offer changed (or was never loaded): start from what the event
      // offers now and keep the counts the customer already chose.
      const counts = new Map(
        current.map((service) => [Number(service.id), service.count ?? 0])
      );
      const services = offered.map((service) => ({
        ...service,
        count: counts.get(Number(service.id)) ?? 0,
      }));

      // Empty the list for one tick first, so every service row is rendered
      // afresh. Swapping the array in place leaves rows that x-for moved
      // showing the old service's price, and their checkbox and count
      // selects are only set up when a row is first rendered.
      item.optionalServices = [];
      await this.$nextTick();
      item.optionalServices = services;
    }

    const descriptionChanged =
      item.start !== event.start_date ||
      item.city !== event.city ||
      item.event_type !== event.event_type ||
      item.language !== event.language;

    // "Prezenčně (Brno)" names the city the cart knew; the server rewrites it
    // to the event's, so show the customer the same.
    if (
      item.city !== event.city &&
      event.city &&
      typeof item.selectedForm === 'string'
    ) {
      item.selectedForm = item.selectedForm.replace(
        /\s*\([^()]*\)$/,
        ` (${event.city})`
      );
    }

    item.price = event.price;
    item.price_eur = event.price_eur;
    item.title = event.name;
    item.start = event.start_date;
    item.city = event.city;
    item.language = event.language;
    item.event_type = event.event_type;
    item.custom_duration = event.custom_duration;
    // Worked out by the server (CartPricingService::serviceDuration), so the
    // cart and the charge can never disagree on it.
    item.duration = event.service_duration;

    return descriptionChanged;
  },

  // Services the last refresh dropped from an item. Kept on the page, not in
  // the saved cart, so the notice is shown when it happens and not on every
  // later visit.
  removedServicesFor(product) {
    return this.removedServices[product.id] ?? [];
  },

  dismissRemovedServices() {
    this.removedServices = {};
  },

  serviceLengthMultiplier(product, service) {
    const serviceId = Number(service.id);
    if (serviceId === 1 || serviceId === 2) {
      const days = Math.trunc(Number(product.duration)) || 0;
      return days > 0 ? days : 1;
    } else {
      return 1;
    }
  },

  get totalPrice() {
    if (this.cart.length === 0) {
      return 0;
    }
    return this.cart.reduce((acc, item) => {
      return (
        acc +
        item.price * item.attendees +
        item.optionalServices.reduce((acc, service) => {
          if (service.count) {
            return (
              acc +
              service.price *
                service.count *
                this.serviceLengthMultiplier(item, service)
            );
          } else {
            return acc;
          }
        }, 0)
      );
    }, 0);
  },

  // What the selected services of a given VAT regime cost (see the server's
  // VatRegimes): a book is 0 %, every other service is taxed like the course.
  servicesTotalByRegime(regime) {
    return this.cart.reduce(
      (acc, item) =>
        acc +
        (item.optionalServices || []).reduce(
          (sum, service) =>
            service.count && service.vat_regime === regime
              ? sum +
                service.price *
                  service.count *
                  this.serviceLengthMultiplier(item, service)
              : sum,
          0
        ),
      0
    );
  },

  get hasBookInCart() {
    return this.servicesTotalByRegime('book') > 0;
  },

  // The displayed total has to follow the same rule the server charges by
  // (VatResolver::orderBreakdown): VAT only on the lines taxed like the
  // course, rounded to a whole crown and added to the net total.
  get totalWithVat() {
    // Whole crowns first, like the server: the net as a whole, and the VAT
    // base on its own, before the rate is applied.
    const net = Math.round(this.totalPrice);
    const vatBase = Math.round(this.totalPrice - this.servicesTotalByRegime('book'));
    const vatAmount = Math.round((vatBase * this.vatRate) / 100);
    return net + vatAmount;
  },

  // `vat_payer` is a toggle field, which casts any non-empty string to true,
  // and the radios store "true"/"false" strings — send 1 or 0 instead.
  get vatPayerInput() {
    const value = this.billingInfo?.vatPayer;
    return value === true || value === 'true' || value === 1 || value === '1'
      ? 1
      : 0;
  },

  get isVatPayer() {
    return this.vatPayerInput === 1;
  },

  // Billed from an EU country other than Czechia, where a VAT ID can mean a
  // reverse charge.
  get billedFromReverseChargeCountry() {
    return REVERSE_CHARGE_COUNTRIES.includes(this.billingInfo?.country);
  },

  // A VAT payer abroad has to give their VAT ID — the reverse charge depends
  // on it. Same rule as the PRINCE2.cz cart.
  get vatNumberRequired() {
    return this.isVatPayer && this.billedFromReverseChargeCountry;
  },

  get reverseCharged() {
    return this.vatRate === 0;
  },

  // Asks the server what VAT applies to the entered VAT ID. Debounced,
  // because it fires while the customer is still typing.
  refreshVatRate() {
    clearTimeout(this.vatRateTimeout);

    // Only a VAT payer's VAT ID counts — the server ignores it otherwise.
    const vatNumber = this.isVatPayer ? this.billingInfo?.dic : null;
    const country = this.billingInfo?.country;

    if (!vatNumber || !country) {
      this.vatRateRequestId++;
      this.vatRate = DEFAULT_VAT_RATE;
      this.vatCheck = { state: null, name: null };
      return;
    }

    // Only a VAT ID from another EU country is sent to VIES; for anyone else
    // there is nothing to verify and nothing to tell.
    const verifiable = this.billedFromReverseChargeCountry;

    // The answer that made this order a reverse charge was about the VAT ID
    // and country as they were before this edit. Until VIES has confirmed the
    // new ones, the summary shows the price with VAT: leaving a reverse charge
    // on screen for a country the customer has just changed quotes them a
    // price they will not be charged, and a VIES lookup can take seconds.
    this.vatRate = DEFAULT_VAT_RATE;
    this.vatCheck = { state: verifiable ? 'checking' : null, name: null };

    // A VIES lookup can take seconds, so a slow earlier answer must not
    // land after a newer one and show a rate for a VAT ID no longer entered.
    const requestId = ++this.vatRateRequestId;
    const isStale = () => requestId !== this.vatRateRequestId;

    this.vatRateTimeout = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/v1/vat-rate?dic=${encodeURIComponent(
            vatNumber
          )}&country=${encodeURIComponent(country)}`,
          { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
        );

        if (isStale()) {
          return;
        }

        if (!response.ok) {
          this.vatRate = DEFAULT_VAT_RATE;
          this.vatCheck = {
            state: verifiable ? 'unavailable' : null,
            name: null,
          };
          return;
        }

        const data = await response.json();

        if (isStale()) {
          return;
        }

        this.vatRate =
          typeof data.rate === 'number' ? data.rate : DEFAULT_VAT_RATE;
        this.vatCheck = {
          state: verifiable ? this.vatCheckState(data) : null,
          name: data.vies_name ?? null,
        };
      } catch (error) {
        // Charging VAT is the safe default when we cannot ask.
        if (!isStale()) {
          this.vatRate = DEFAULT_VAT_RATE;
          this.vatCheck = {
            state: verifiable ? 'unavailable' : null,
            name: null,
          };
        }
      }
    }, VAT_LOOKUP_DEBOUNCE);
  },

  // Maps VatResolver's reason to what the customer is told. Nothing blocks
  // the order: without a verified VAT ID it simply goes out with Czech VAT,
  // and the message says so while there is still time to fix a typo.
  vatCheckState(data) {
    if (data.reverse_charge) {
      return 'verified';
    }

    if (data.reason === 'vies_unavailable') {
      return 'unavailable';
    }

    if (
      data.reason === 'billing_country_mismatch' ||
      data.reason === 'country_not_reverse_charged'
    ) {
      return 'mismatch';
    }

    return 'invalid';
  },

  async getXsfrToken() {
    const response = await fetch(BASE_URL_SANCTUM);
    if (!response.ok) {
      throw new Error(`Error! status: ${response.status}`);
    }
  },

  get getCartInput() {
    if (this.cart.length > 0) {
      const ret = this.cart.map((item) => ({
        title: item.title,
        start: item.start,
        attendeesList: item.attendeesList,
        selectedForm: item.selectedForm,
        optionalServices: item.optionalServices
          .filter((service) => service.count && service.count > 0)
          .map(({ count, ...rest }) => ({
            ...rest,
            service_count: count,
          })),
        city: item.city,
        duration: item.duration,
        language: item.language,
        courseId: item.id,
        id: item.id,
      }));
      return JSON.stringify(ret);
    }
    return null;
  },

  createAttendeesList() {
    this.cart.forEach((item) => {
      const attendeeCount = item.attendees;

      if (!item.attendeesList) {
        item.attendeesList = [];
      }

      item.attendeesList.length = attendeeCount;

      for (let i = 0; i < attendeeCount; i++) {
        if (!item.attendeesList[i]) {
          item.attendeesList[i] = {
            email: '',
            name: '',
          };
        }
      }
    });
  },

  handleServiceSelect() {
    const select = $(this.$el).find('select');
    select.SumoSelect();
    for (let i = 1; i <= this.product.attendees; i++) {
      select[0].sumo.add(i);
    }

    //set service amount from storage
    if (this.service.count > 0) {
      select[0].sumo.selectItem(this.service.count - 1);
      select[0].sumo.reload();
    }

    //update service select when number of attendees changes
    this.$watch('product.attendees', (value, oldValue) => {
      //remove all options and add new number of options
      for (let i = 0; i < oldValue; i++) {
        select[0].sumo.remove(0);
      }
      for (let i = 1; i <= value; i++) {
        select[0].sumo.add(i);
      }

      //if service is checked set max amount
      if (this.checked) {
        select[0].sumo.selectItem(this.product.attendees - 1);
        this.service.count = this.product.attendees;
        select[0].sumo.reload();
        return;
      }
    });

    //watch if service is checked. If not set ammount to 0 otherwise set it to max amount
    this.$watch('checked', (value) => {
      this.service.count = value ? this.product.attendees : 0;
      this.$nextTick(() => {
        select[0].sumo.reload();
      });
    });
  },

  removeProduct(index) {
    this.cart.splice(index, 1);
    this.$dispatch('cart-updated', true);
  },

  formatedMoney(czk, eur) {
    return formatedMoneyWithFree(czk, eur);
  },

  formatedDay(event) {
    return formatedDay(event.start).replace('.', '');
  },

  formatedMonth(event) {
    return formatedMonth(event.start);
  },

  validateInputs(form) {
    if (!form.checkValidity()) {
      var tmpSubmit = document.createElement('button');
      form.appendChild(tmpSubmit);
      tmpSubmit.click();
      form.removeChild(tmpSubmit);
      return false;
    }
    return true;
  },

  emailInput(index) {
    if (this.product.attendeesList[index]) {
      return this.product.attendeesList[index].email;
    }
  },

  initDateSelect() {
    const select = $(this.$el).find('select');
    const courseDate =
      this.formatedDay(this.product) + '. ' + this.formatedMonth(this.product);
    select.SumoSelect();
    select[0].sumo.add(courseDate);
    select[0].sumo.selectItem(0);
    select[0].sumo.reload();
  },

  initFormSelect(transInPerson, transOnline) {
    const select = $(this.$el).find('select');
    select.SumoSelect();
    const inPersonText = `${transInPerson} (${this.product.city})`;
    if (this.product.event_type == IN_PERSON_TYPE) {
      select[0].sumo.add(inPersonText);
    } else if (this.product.event_type == BOTH_TYPE) {
      select[0].sumo.add(inPersonText);
      select[0].sumo.add(transOnline);
    } else {
      select[0].sumo.add(transOnline);
    }
    if (this.product.selectedForm) {
      select[0].sumo.selectItem(this.product.selectedForm);
    } else {
      select[0].sumo.selectItem(0);
    }
    select[0].sumo.reload();
  },

  initLanguageSelect(cs, en) {
    const select = $(this.$el).find('select');
    select.SumoSelect();
    if (this.product.language === 'cs') {
      select[0].sumo.add(cs);
    } else {
      select[0].sumo.add(en);
    }
    select[0].sumo.selectItem(0);
    select[0].sumo.reload();
  },

  initAttendeesSelect() {
    const select = $(this.$el).find('select');
    select.SumoSelect();
    this.$nextTick(() => {
      select[0].sumo.reload();
    });
  },

  initBillingInfo() {
    const defaults = {
      name: '',
      company: '',
      email: '',
      phone: '',
      ico: '',
      dic: '',
      street: '',
      city: '',
      zip: '',
      country: DEFAULT_COUNTRY,
      countryOther: '',
      internalCode: '',
      vatPayer: false,
    };

    const billingInfo = localStorage.getItem('billingInfo');

    // Spread over the defaults so a billingInfo stored before a new field
    // existed does not come back missing it.
    this.billingInfo = billingInfo
      ? { ...defaults, ...JSON.parse(billingInfo) }
      : defaults;
  },

  initInputs() {
    initInputs();
  },

  clearCart() {
    localStorage.removeItem('cart');
    localStorage.removeItem('billingInfo');
    this.$dispatch('cart-updated');
  },

  init() {
    if (this.step === 4) {
      this.step = 1;
    }

    // Check for payment cancellation or error
    const urlParams = new URLSearchParams(window.location.search);
    const paymentCancelled = urlParams.get('payment_cancelled');
    const paymentError = urlParams.get('payment_error');
    const paymentSuccess = urlParams.get('success');
    const stepParam = urlParams.get('step');

    if (paymentSuccess === 'true') {
      this.cart = [];
      this.clearCart();
      this.step = 1;
      const newUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, '', newUrl);
    } else {
      this.getCart();
      if (
        this.cart.length > 0 &&
        this.step > 2 &&
        !localStorage.getItem('billingInfo')
      ) {
        this.step = 1;
      }
    }

    this.$watch('cart', (value) => {
      writeStoredCart(value);
      this.abandonPendingCheckout();
    });

    if (paymentCancelled === 'true' || paymentError) {
      if (stepParam) {
        this.step = parseInt(stepParam);
      } else {
        this.step = 3;
      }

      this.$nextTick(() => {
        const cardOption = document.querySelector(
          '[name="payment_option"][data-payment-type="card"]'
        );
        if (cardOption) {
          cardOption.checked = true;
          this.paymentMethod = cardOption.value;
        }

        this.showOrderError(
          paymentCancelled === 'true' ? 'payment_cancelled' : paymentError
        );

        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      });
    }

    this.$nextTick(() => {
      initInputs();
      this.createAttendeesList();
    });
    this.initBillingInfo();
    this.$watch('billingInfo', (value) => {
      localStorage.setItem('billingInfo', JSON.stringify(value));
      this.abandonPendingCheckout();
    });
    this.refreshVatRate();
    this.$watch('billingInfo.dic', () => this.refreshVatRate());
    this.$watch('billingInfo.country', () => this.refreshVatRate());
    this.$watch('billingInfo.vatPayer', () => this.refreshVatRate());
  },

  // The customer changed the cart or the billing details after the server
  // re-priced their order (total_changed): it is a different order now. The
  // re-priced one stays unpaid, like any checkout left on Stripe, and the
  // form is sent again under a new id — the old id is taken, so reusing it
  // would only be refused.
  abandonPendingCheckout() {
    if (!this.checkoutPendingId) {
      return;
    }

    this.checkoutPendingId = null;
    this.customFormId = newCustomFormId();
  },

  // Shows the message for an error key the server or the checkout returned.
  // Anything unknown falls back to the generic payment error.
  showOrderError(key) {
    this.paymentErrorKey = ORDER_ERROR_KEYS.includes(key) ? key : null;

    if (this.paymentErrorTimeout) {
      clearTimeout(this.paymentErrorTimeout);
    }

    this.showPaymentError = true;

    this.paymentErrorTimeout = setTimeout(() => {
      this.showPaymentError = false;
      this.paymentErrorTimeout = null;
    }, PAYMENT_ERROR_TIMEOUT);
  },

  // The form endpoint answers a refused order with a 400 whose `error`
  // object is keyed by the reason (PriceCourseOrder).
  orderErrorKey(errors) {
    return (
      Object.keys(errors ?? {}).find((key) => ORDER_ERROR_KEYS.includes(key)) ??
      null
    );
  },

  // Called when the non-card form submission is refused.
  handleOrderRejected(errors) {
    const key = this.orderErrorKey(errors);
    this.showOrderError(key);

    if (key === 'price_changed') {
      this.cartRefresh = this.refreshCart();
    }
  },

  // Waits for the cart to be brought up to date before anything is sent, so
  // an order never goes out with a price the server is about to refuse — and
  // stops if that refresh changed what the customer was looking at.
  //
  // A refresh that could not finish (a rate limit, a slow CRM) does not stop
  // the order: the server asks the events API afresh when the form arrives
  // and refuses anything it cannot price, a card payment included.
  async ensureCartIsCurrent() {
    const shownTotal = this.totalPrice;

    let result = await this.cartRefresh;

    const stale = Date.now() - this.cartRefreshedAt > CART_REFRESH_MAX_AGE;

    if (!result.complete || stale) {
      this.cartRefresh = this.refreshCart();
      result = await this.cartRefresh;
    }

    // The server refuses an event it cannot find, whatever the payment.
    if (result.notFound) {
      this.showOrderError('pricing_failed');
      return false;
    }

    if (Math.round(this.totalPrice) !== Math.round(shownTotal)) {
      this.showOrderError('price_changed');
      return false;
    }

    return true;
  },

  // The payment radios carry a fixed code; their value is the translated
  // label, which is only for people.
  get paymentType() {
    const selected = [
      ...document.querySelectorAll('[name="payment_option"]'),
    ].find((option) => option.value === this.paymentMethod);
    return selected?.dataset.paymentType ?? '';
  },

  async checkPaymentAndSubmit(sendCallback) {
    const isCard = this.paymentType === 'card';

    this.showPaymentError = false;
    this.checkingCart = true;
    let ready = false;

    try {
      ready = await this.ensureCartIsCurrent();
    } catch (error) {
      // Never leave the button spinning. The order goes on: the server
      // checks its price regardless.
      console.error('Could not check the cart:', error);
      ready = true;
    } finally {
      this.checkingCart = false;
    }

    if (!ready) {
      return false;
    }

    if (isCard) {
      this.redirectingToStripe = true;

      // The customer has seen the re-priced total of an order already sent;
      // confirming it pays that same order.
      if (this.checkoutPendingId && this.checkoutPendingId === this.customFormId) {
        this.startCheckout();
        return false;
      }

      const result = await this.submitFormSilently();

      if (!result.ok) {
        this.redirectingToStripe = false;
        this.handleOrderRejected(result.errors);
        return false;
      }

      this.startCheckout();
      return false;
    } else {
      if (sendCallback) {
        sendCallback();
      }
      return true;
    }
  },

  async submitFormSilently() {
    const form = document.querySelector('form.cart__content');
    if (!form) return { ok: false, errors: {} };

    try {
      await this.getXsfrToken();

      const data = new URLSearchParams(new FormData(form));
      const recaptchaAction = form.action.replace(/[^A-Za-z]/g, '');
      let recaptchaToken = 'dev-token';

      if (
        RECAPTCHA_SITE_KEY &&
        RECAPTCHA_SITE_KEY !== 'your_recaptcha_site_key_here'
      ) {
        const recaptcha = await load(RECAPTCHA_SITE_KEY);
        recaptchaToken = await recaptcha.execute(recaptchaAction);
      }

      data.append('recaptcha_token', recaptchaToken);
      data.append('recaptcha_action', recaptchaAction);

      const response = await fetch(form.action, {
        body: data,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-XSRF-TOKEN': docCookies.getItem('XSRF-TOKEN'),
        },
        method: 'POST',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        return { ok: false, errors: body.error ?? {} };
      }

      return { ok: true, errors: {} };
    } catch (error) {
      console.error('Error submitting form:', error);
      return { ok: false, errors: {} };
    }
  },

  async startCheckout() {
    this.redirectingToStripe = true;

    const failWith = (key) => {
      this.redirectingToStripe = false;
      // Reloading step 3 refreshes the cart, so a changed price is on screen
      // by the time the message is.
      const currentUrl = window.location.pathname;
      window.location.href = `${currentUrl}?step=3&payment_error=${encodeURIComponent(
        key
      )}`;
    };

    try {
      await this.getXsfrToken();

      const response = await fetch('/api/v1/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-XSRF-TOKEN': docCookies.getItem('XSRF-TOKEN'),
        },
        body: JSON.stringify({
          // The server prices the order itself from the submitted cart, not
          // from anything sent here — see #524.
          custom_id: this.customFormId,
          // The API has no locale prefix; this sends the customer back to
          // this site's cart or thank you page.
          site: document.documentElement.lang,
        }),
      });

      const data = await response.json();

      // The server re-priced the order under today's rules: show the new
      // total here and let the customer confirm it — the same order, no
      // reload (a reload would give the form a new id and a second order).
      if (data.error === 'total_changed') {
        this.redirectingToStripe = false;
        this.totalChangedGross = data.gross ?? '';
        this.checkoutPendingId = this.customFormId;
        this.showOrderError('total_changed');
        return;
      }

      if (!response.ok) {
        failWith(CHECKOUT_ERROR_KEYS[data.error] ?? 'true');
        return;
      }

      if (!data.url) {
        throw new Error('No checkout URL received from server');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      failWith('true');
    }
  },
});
